import { QRCodeSVG } from "qrcode.react";
import { Copy, CreditCard, MessageCircle, Smartphone, CheckCircle2, Clock } from "lucide-react";
import { inr, waLink } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { usePaymentOptions } from "@/hooks/usePaymentOptions";

const Option = ({ icon: Icon, title, subtitle, active, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-terracotta bg-saffron-subtle/60" : "border-cream-muted bg-cream hover:border-ragi/30"}`}>
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${active ? "bg-terracotta text-white" : "bg-ragi/10 text-ragi"}`}><Icon className="h-5 w-5" /></span>
    <span className="flex-1"><span className="block text-sm font-semibold text-ragi-dark">{title}</span><span className="block text-xs text-muted-foreground">{subtitle}</span></span>
  </button>
);

export default function PaymentOptions({ order, waNumber, waMessage, onUpdated }) {
  const { methods, choice, setChoice, upi, reference, setReference, busy, error, copy, claimUpi, payRazorpay } = usePaymentOptions(order, onUpdated);

  if (order.payment_status === "paid") {
    return (
      <div className="mt-6 flex items-center gap-3 rounded-2xl bg-ragi/10 p-4 text-ragi" data-testid="payment-paid-banner">
        <CheckCircle2 className="h-6 w-6 shrink-0" />
        <div><p className="text-sm font-semibold">Payment received — thank you!</p><p className="text-xs text-ragi/80">We'll confirm your fresh batch on WhatsApp.</p></div>
      </div>
    );
  }
  if (order.payment_status === "verification_pending") {
    return (
      <div className="mt-6 flex items-center gap-3 rounded-2xl bg-saffron-subtle p-4 text-ragi-dark" data-testid="payment-verification-banner">
        <Clock className="h-6 w-6 shrink-0 text-saffron-dark" />
        <div><p className="text-sm font-semibold">UPI payment reference received</p><p className="text-xs text-ragi-dark/70">Ref {order.upi_reference}. We're verifying it and will confirm on WhatsApp.</p></div>
      </div>
    );
  }
  if (order.status === "cancelled") return <p className="mt-6 text-center text-sm text-muted-foreground" data-testid="payment-cancelled-message">This order has been cancelled.</p>;
  if (!methods) return <p className="mt-6 text-center text-xs text-muted-foreground" data-testid="payment-options-loading">Loading payment options…</p>;

  return (
    <div className="mt-6" data-testid="payment-options">
      {error && <p role="alert" className="mb-3 text-xs text-terracotta" data-testid="payment-options-error">{error}</p>}
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-terracotta">Choose how to pay</p>
      {methods.razorpay.enabled && methods.razorpay.test_mode && <p className="mb-3 text-xs text-terracotta" data-testid="razorpay-test-mode">Card checkout is in test mode. No real payment will be collected.</p>}
      <div className="space-y-2">
        {methods.upi.enabled && <Option icon={Smartphone} title="Pay now via UPI" subtitle="GPay · PhonePe · Paytm · any UPI app" active={choice === "upi"} onClick={() => setChoice("upi")} testId="pay-option-upi" />}
        {methods.razorpay.enabled && <Option icon={CreditCard} title="Pay with card / netbanking" subtitle="Secure checkout by Razorpay" active={choice === "razorpay"} onClick={() => setChoice("razorpay")} testId="pay-option-razorpay" />}
        <Option icon={MessageCircle} title="Confirm on WhatsApp" subtitle="Pay on delivery / pickup" active={choice === "whatsapp"} onClick={() => setChoice("whatsapp")} testId="pay-option-whatsapp" />
      </div>

      {choice === "upi" && upi && (
        <div className="mt-4 rounded-2xl border border-cream-muted bg-cream p-5" data-testid="upi-panel">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <div className="rounded-2xl bg-white p-3 shadow-sm" data-testid="upi-qr"><QRCodeSVG value={upi.link} size={148} fgColor="#2D4A3E" /></div>
            <div className="flex-1 text-center sm:text-left">
              <p className="font-display text-2xl font-bold text-ragi-dark">{inr(upi.amount)}</p>
              <p className="mt-1 text-xs text-muted-foreground">Scan the QR or pay to</p>
              <button onClick={() => copy(upi.vpa)} className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-cream-secondary px-3 py-1.5 font-mono text-xs font-semibold text-ragi-dark" data-testid="upi-vpa-copy">{upi.vpa} <Copy className="h-3 w-3" /></button>
              <p className="mt-2 text-[11px] text-muted-foreground">Add note: <span className="font-semibold text-ragi-dark">{upi.note}</span></p>
              <a href={upi.link} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ragi-dark py-3 text-sm font-bold text-cream sm:hidden" data-testid="upi-app-link"><Smartphone className="h-4 w-4" /> Open UPI app</a>
            </div>
          </div>
          <div className="mt-5 border-t border-cream-muted pt-4">
            <p className="mb-2 text-xs font-semibold text-ragi-dark">After paying, enter the UPI transaction ID (UTR)</p>
            <div className="flex gap-2">
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 412345678901" className="bg-cream-secondary" data-testid="upi-reference-input" />
              <button onClick={claimUpi} disabled={busy} className="shrink-0 rounded-full bg-terracotta px-5 text-sm font-semibold text-white disabled:opacity-60" data-testid="upi-submit-button">{busy ? "…" : "I've paid"}</button>
            </div>
          </div>
        </div>
      )}

      {choice === "razorpay" && (
        <button onClick={payRazorpay} disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ragi-dark py-4 text-sm font-bold text-cream transition-transform hover:scale-[1.02] disabled:opacity-60" data-testid="razorpay-pay-button">
          <CreditCard className="h-5 w-5" /> Pay {inr(order.total)} securely
        </button>
      )}

      {choice === "whatsapp" && waNumber && (
        <a href={waLink(waNumber, waMessage)} target="_blank" rel="noopener noreferrer" data-testid="confirm-whatsapp-button" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-sm font-bold text-white shadow-lg transition-transform hover:scale-[1.02]">
          <MessageCircle className="h-5 w-5" /> Confirm Order on WhatsApp
        </a>
      )}
    </div>
  );
}
